import { ref, computed, type Ref, type ComputedRef } from 'vue'
import {
	useRoute,
	useRouter,
	type RouteLocationNormalizedLoaded,
} from 'vue-router'
import type { ModalData, ModalOptions, ModalInstance } from '@/types/modal.ts'

const activeModals: Ref<Map<string, ModalData>> = ref(new Map())

export function useModal() {
	const route: RouteLocationNormalizedLoaded = useRoute()
	const router = useRouter()

	// Open a modal with typed data
	const openModal = <T extends ModalData>(
		modalName: string,
		data: T = {} as T,
		options: ModalOptions = {},
		params?: Record<string, any>
	): number => {
		const modalData: T & { _timestamp: number } = {
			...data,
			_timestamp: Date.now(),
		}

		// Store modal data globally
		activeModals.value.set(modalName, modalData)
		// Update route query params if route sync is enabled (default: true)
		if (options.syncWithRoute !== false) {
			const queryParams = params || route.query
			const mergedParams = {
				...queryParams,
				modal: modalName,
				...flattenForQuery(data),
			}
			router.push({ query: mergedParams })
		}

		return modalData._timestamp
	}

	const closeModal = (modalName: string, options: ModalOptions = {}): void => {
		// Update route if sync is enabled
		if (options.syncWithRoute !== false) {
			const { modal, ...queryWithoutModal } = route.query

			if (typeof modalName === 'undefined') {
				modalName = route.query?.modal as string
			}
			const { origin } = activeModals.value.get(modalName) as ModalData
			const hash = origin.indexOf('#') > -1 ? '#' + origin.split('#')[1] : ''
			const cleanedQuery = removeModalParams(queryWithoutModal, modalName)
			activeModals.value.delete(modalName)

			try {
				router.push({
					query: cleanedQuery,
					hash,
				})
			} catch (error) {
				console.error('Invalid origin URL:', origin, error)
			}
		} else {
			activeModals.value.delete(modalName)
		}
	}

	const closeAllModals = (): void => {
		activeModals.value.clear()
		const { modal, ...cleanQuery } = route.query
		router.push({ query: cleanQuery })
	}

	const isModalOpen = (modalName: string): boolean => {
		return activeModals.value.has(modalName) || route.query.modal === modalName
	}

	const getActiveModalInstances = (): ComputedRef<ModalInstance[]> => {
		return computed(() => {
			return Array.from(activeModals.value.entries()).map(([name, data]) => ({
				name,
				data,
				timestamp: data._timestamp || 0,
			}))
		})
	}

	const flattenForQuery = (
		data: Record<string, any>
	): Record<string, string> => {
		const flattened: Record<string, string> = {}

		Object.entries(data).forEach(([key, value]) => {
			if (key.startsWith('_')) return // Skip internal properties

			if (typeof value === 'object' && value !== null) {
				flattened[key] = JSON.stringify(value)
			} else if (value !== undefined && value !== null) {
				flattened[key] = String(value)
			}
		})

		return flattened
	}

	const removeModalParams = (
		query: Record<string, any>,
		modalName?: string
	): Record<string, string> => {
		const defaultParamsToRemove = ['origin', 'modal']
		modalName = modalName || (query.modal as string)
		const queryParams = activeModals.value.get(modalName)
		const paramsToRemove = [
			...Object.keys(queryParams || {}),
			...defaultParamsToRemove,
		]
		const cleaned = { ...query }
		paramsToRemove.forEach((param) => {
			delete cleaned[param]
		})

		return cleaned
	}

	return {
		openModal,
		closeModal,
		closeAllModals,
		isModalOpen,
		removeModalParams,

		activeModals: computed(() => activeModals.value),
		activeModalInstances: getActiveModalInstances(),
	}
}
